import dayjs from 'dayjs';
import i18n from '../i18n/config';

export type SLAStatus = 'safe' | 'warning' | 'critical' | 'overdue';

export interface SLACalculation {
  hours: number;
  minutes: number;
  seconds: number;
  isOverdue: boolean;
  status: SLAStatus;
  displayText: string;
  color: string;
  backgroundColor: string;
}

/**
 * Calculate SLA status based on deadline
 * @param deadline - ISO 8601 date string or Date object
 * @param complaintStatus - Current complaint status
 * @returns SLA calculation object with time remaining, status, and display properties
 */
export const calculateSLAStatus = (
  deadline: string | Date,
  complaintStatus?: string
): SLACalculation => {
  const now = dayjs();
  const deadlineDate = dayjs(deadline);
  const diff = deadlineDate.diff(now);
  const isOverdue = diff < 0;

  // Calculate hours, minutes, and seconds
  const absDiff = Math.abs(diff);
  const hours = Math.floor(absDiff / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);

  // Determine status based on hours remaining
  let status: SLAStatus;
  if (isOverdue) {
    status = 'overdue';
  } else if (hours < 2) {
    status = 'critical';
  } else if (hours < 6) {
    status = 'warning';
  } else {
    status = 'safe';
  }

  // Get localized display text
  const displayText = formatSLADisplayText(hours, minutes, seconds, isOverdue, complaintStatus);

  // Get color scheme based on status
  const { color, backgroundColor } = getSLAColors(status);

  return {
    hours,
    minutes,
    seconds,
    isOverdue,
    status,
    displayText,
    color,
    backgroundColor,
  };
};

/**
 * Format SLA display text based on locale
 * @param hours - Hours remaining/overdue
 * @param minutes - Minutes remaining/overdue
 * @param seconds - Seconds remaining/overdue
 * @param isOverdue - Whether the deadline has passed
 * @param complaintStatus - Current complaint status
 * @returns Formatted display text
 */
export const formatSLADisplayText = (
  hours: number,
  minutes: number,
  seconds: number,
  isOverdue: boolean,
  complaintStatus?: string
): string => {
  const lang = i18n.language || 'en';

  // If complaint is resolved, closed, or closed_by_admin, show completion message
  if (complaintStatus === 'resolved' || complaintStatus === 'closed' || complaintStatus === 'closed_by_admin') {
    if (lang === 'ar') {
      return complaintStatus === 'closed_by_admin' ? 'مغلقة من الإدارة' : 'تم الإنجاز في الموعد المحدد';
    } else if (lang === 'fr') {
      return complaintStatus === 'closed_by_admin' ? 'Fermée par Admin' : 'Terminé dans le délai imparti';
    } else {
      return complaintStatus === 'closed_by_admin' ? 'Closed by Admin' : 'Completed within deadline';
    }
  }

  // Format time display with hours:minutes:seconds
  // Pad numbers with leading zeros for better readability
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (lang === 'ar') {
    if (isOverdue) {
      return `متأخر بمقدار ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      return `${hours}:${paddedMinutes}:${paddedSeconds} متبقي`;
    }
  } else if (lang === 'fr') {
    if (isOverdue) {
      return `En retard de ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      return `${hours}:${paddedMinutes}:${paddedSeconds} restant`;
    }
  } else {
    if (isOverdue) {
      return `OVERDUE by ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      return `${hours}:${paddedMinutes}:${paddedSeconds} remaining`;
    }
  }
};

/**
 * Get color scheme based on SLA status
 * @param status - Current SLA status
 * @returns Object containing text color and background color
 */
export const getSLAColors = (
  status: SLAStatus
): { color: string; backgroundColor: string } => {
  switch (status) {
    case 'safe':
      return {
        color: '#2e7d32', // Dark green
        backgroundColor: '#c8e6c9', // Light green
      };
    case 'warning':
      return {
        color: '#f57c00', // Dark orange
        backgroundColor: '#ffe0b2', // Light orange
      };
    case 'critical':
      return {
        color: '#c62828', // Dark red
        backgroundColor: '#ffcdd2', // Light red
      };
    case 'overdue':
      return {
        color: '#ffffff', // White text
        backgroundColor: '#d32f2f', // Red background
      };
    default:
      return {
        color: '#616161', // Grey
        backgroundColor: '#f5f5f5', // Light grey
      };
  }
};

/**
 * Get icon name based on SLA status (for Material-UI icons)
 * @param status - Current SLA status
 * @returns Icon component name
 */
export const getSLAIcon = (status: SLAStatus): string => {
  switch (status) {
    case 'safe':
      return 'CheckCircle';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Error';
    case 'overdue':
      return 'Cancel';
    default:
      return 'Schedule';
  }
};

/**
 * Check if SLA should be displayed (not for completed/closed complaints)
 * @param status - Current complaint status
 * @returns Whether to show the SLA countdown
 */
export const shouldShowSLACountdown = (status: string): boolean => {
  return !['resolved', 'closed', 'closed_by_admin'].includes(status);
};

/**
 * Calculate percentage of time remaining (for progress bars)
 * @param deadline - SLA deadline
 * @param slaHours - Total SLA hours allocated
 * @returns Percentage (0-100)
 */
export const calculateSLAPercentage = (
  deadline: string | Date,
  slaHours: number
): number => {
  const now = dayjs();
  const deadlineDate = dayjs(deadline);
  const diff = deadlineDate.diff(now);

  if (diff < 0) return 0; // Overdue

  const remainingHours = diff / (1000 * 60 * 60);
  const percentage = (remainingHours / slaHours) * 100;

  return Math.max(0, Math.min(100, percentage));
};
